import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Alert,
  Collapse
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft,
  ChevronRight,
  Dashboard,
  Flight,
  AirplanemodeActive,
  Build,
  People,
  ExitToApp,
  ShoppingCart,
  Assignment,
  Person,
  History,
  AssignmentInd,
  Inventory2,
  Feedback,
  Assessment,
  Settings,
  Description,
  BugReport,
  MenuBook,
  Book,
  AdminPanelSettings,
  PrecisionManufacturing,
  Category,
  Security,
  Add,
  Engineering,
  ReceiptLong,
  ExpandLess,
  ExpandMore,
  FlightTakeoff,
  Inbox,
  SwapHoriz,
  LocalShipping,
  School,
  SupportAgent,
  Groups,
  WorkspacePremium,
  Campaign
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import NotificationsBell from './notifications/NotificationsBell';
import FloatingFeedbackButton from './feedback/FloatingFeedbackButton';
import { ROLES, ALL_ROLES } from '../constants/roles';
import { roleMatches } from '../utils/rolePermissions';
import { getModuleKeyFromPath, canViewModuleByGrant } from '../utils/viewPermissions';
import fleetwrightLogo from '../assets/fleetwright-logo.png';
import axios from 'axios';

const AUTHORIZATION_MENU_ROLES = [...ALL_ROLES];
const isAuthorizationNavItem = (item) =>
  item?.text === 'Authorization' || (typeof item?.path === 'string' && item.path.startsWith('/authorization'));

const drawerWidth = 240;
const miniDrawerWidth = 72;
const drawerBackground = '#f5f5f5';

const MENU_ICON_COLORS = {
  Dashboard: '#1976d2',
  'AM Dashboard': '#7b1fa2',
  'AM MRO Dashboard': '#5e35b1',
  'Head of Operations Dashboard': '#c05621',
  'Crew Duty': '#00897b',
  'Staff details': '#5c6bc0',
  'Staff training': '#5c6bc0',
  'Flight Entry': '#2e7d32',
  'Operations Board': '#ed6c02',
  CAMO: '#1565c0',
  'CAMO Dashboard': '#1976d2',
  'Aircraft Life-cycle Management': '#5d4037',
  'Master Data Management': '#6a1b9a',
  'Maintenance & Inspections': '#e65100',
  'AMP Schedules': '#f57c00',
  'AD/SB Service Bulletin': '#c62828',
  'Technical Log Book': '#0277bd',
  'Technical Log Book Format': '#455a64',
  Planning: '#6a1b9a',
  'PROCUREMENT AND STORE': '#f57c00',
  'Store Activities': '#2e7d32',
  'Receiving and Inspection': '#0288d1',
  Issuance: '#00897b',
  Procurement: '#ed6c02',
  'Store Inventory': '#2e7d32',
  'Store Returns': '#00897b',
  Loan: '#7b1fa2',
  ADMIN: '#c62828',
  User: '#1976d2',
  'User Onboarding': '#00897b',
  People: '#00897b',
  'Client Settings': '#546e7a',
  'Audit Logs': '#6d4c41',
  'MIS Dashboard': '#7b1fa2',
  'Roles Glossary': '#d32f2f',
  'Crew Inbox': '#0288d1',
  'Operator Portal': '#5c6bc0',
  Aircraft: '#1565c0',
  'Serialized Item Lifecycle': '#5d4037',
  'Parts Catalog': '#00695c',
  'MRO Workbench': '#e65100',
  Authorization: '#6a1b9a',
  'MRO Workbench Lab': '#e65100',
  'Lab Overview': '#e65100',
  'Lab Planning': '#1565c0',
  'Lab Manager': '#e65100',
  'Lab Engineer': '#2e7d32',
  'Lab Quality': '#6a1b9a',
  'My Profile': '#455a64',
  'DGCA Baseline': '#546e7a',
  'Operational Queries': '#2e7d32',
  'PDF Field Mappings': '#283593',
  'PDF Template Testing': '#ad1457',
  'Aviation Glossary': '#6d4c41',
  'Fleetwright Tutorials': '#9c27b0',
  'Report Center': '#5e35b1',
  'Spect Control': '#6a1b9a',
  'Technical Support': '#0277bd',
  'Notice Board': '#c62828',
  'Quick Add': '#388e3c',
  'Fleet Portal': '#1976d2',
  'My Aircraft': '#1565c0',
};

const menuIconSx = (text) => {
  const color = MENU_ICON_COLORS[text];
  if (!color) return {};
  return {
    color,
    '.Mui-selected &': { color },
  };
};

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isSidebarMinimized, setIsSidebarMinimized] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [hangarProductMode, setHangarProductMode] = useState('SUITE');
  const [hrmsEnabled, setHrmsEnabled] = useState(false);
  const { user, logout, isReadOnly, viewGrants } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!user) return;
    axios.get('/api/mro/hangar-settings')
      .then((res) => setHangarProductMode(res.data?.productMode || 'SUITE'))
      .catch(() => setHangarProductMode('SUITE'));
    axios.get('/api/hrms/status')
      .then((res) => setHrmsEnabled(Boolean(res.data?.enabled && res.data?.canAccess)))
      .catch(() => setHrmsEnabled(false));
  }, [user]);

  const isHangarSku = hangarProductMode === 'HANGAR';
  /** Hangar SKU trims nav for most roles; Super Admin, Spectator, AM, Planning, and CAMO QM keep suite access. */
  const hangarNavBypassRoles = new Set([
    ROLES.SUPER_ADMIN,
    ROLES.SPECTATOR,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.AM_MRO,
    ROLES.PLANNING_MANAGER,
    ROLES.CAMO_QM_MANAGER,
    ROLES.CAMO_QM_OFFICER
  ]);
  const trimHangarNav = isHangarSku && !hangarNavBypassRoles.has(user?.role);
  const hangarHiddenPaths = new Set([
    '/camo-dashboard',
    '/maintenance',
    '/amp-schedules',
    '/adsb-service-bulletin',
    '/technical-log-book',
    '/admin/technical-log-book-format',
    '/crew-duty-roster',
    '/sales',
    '/ops',
    '/am-dashboard',
    '/am-mro-dashboard',
    '/ops-dashboard'
  ]);
  /**
   * Even when Hangar trims CAMO nav, keep Maintenance for roles that use the EDR inbox
   * (CA Manager / CA Technical Officer are not full suite bypass roles).
   */
  const hangarMaintenanceKeptRoles = new Set([
    ROLES.CA_MANAGER,
    ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER ]);
  const hangarMaintenanceKeptPaths = new Set(['/maintenance']);
  /** Ops + Admin/AM always keep Crew Duty Roster and core ops pages in Hangar SKU. */
  const hangarOpsKeptRoles = new Set([
    ROLES.OPERATIONS_MANAGER,
    ROLES.OPERATIONS_EXECUTIVE,
    ROLES.ACCOUNTABLE_MANAGER,
    ROLES.SUPER_ADMIN
  ]);
  const hangarOpsKeptPaths = new Set(['/crew-duty-roster', '/sales', '/ops', '/ops-dashboard']);
  /** CREW keep self-service Crew Duty (roster + hours) in Hangar SKU. */
  const hangarCrewKeptPaths = new Set(['/crew-duty-roster']);
  /** Keep Master Data + Aircraft lifecycle + lite aircraft register in hangar SKU */
  const hangarAllowedCamoChildren = new Set(['/aircraft-lifecycle', '/master-data']);

  const menuItems = [
    { text: 'Notice Board', icon: <Campaign />, path: '/notice-board', roles: [], excludeRoles: [ROLES.CLIENT, ROLES.EXTERNAL_OPERATOR] },
    { text: 'Dashboard', icon: <Dashboard />, path: '/', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.AM_MRO, ROLES.SALES, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.CREW, ROLES.LOAN_PILOT, ROLES.EXTERNAL_PILOT, ROLES.PLANNING_MANAGER, ROLES.PLANNING_OFFICER, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE, ROLES.SAFETY_COFS, ROLES.SAFETY_MANAGER, ROLES.SAFETY_OFFICER, ROLES.MIS] },
    { text: 'AM Dashboard', icon: <Assessment />, path: '/am-dashboard', roles: [ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
    { text: 'AM MRO Dashboard', icon: <Assessment />, path: '/am-mro-dashboard', roles: [ROLES.AM_MRO, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER] },
    { text: 'Head of Operations Dashboard', icon: <Assessment />, path: '/ops-dashboard', roles: [ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
    { text: 'Crew Duty', icon: <Assignment />, path: '/crew-duty-roster', roles: [ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN, ROLES.CREW, ROLES.LOAN_PILOT] },
    {
      text: 'Staff details',
      icon: <Groups />,
      path: '/hrms',
      roles: [
        ROLES.SUPER_ADMIN,
        ROLES.ACCOUNTABLE_MANAGER,
        ROLES.HR_MANAGER,
        ROLES.OPERATIONS_MANAGER,
        ROLES.OPERATIONS_EXECUTIVE,
        ROLES.SALES,
        ROLES.PLANNING_MANAGER,
        ROLES.PLANNING_OFFICER,
        ROLES.CA_MANAGER,
        ROLES.CA_TECHNICAL_OFFICER,
        ROLES.CAMO_QM_MANAGER,
        ROLES.CAMO_QM_OFFICER,
        ROLES.MRO_MANAGER,
        ROLES.MRO_ENGINEER,
        ROLES.MRO_QM_MANAGER,
        ROLES.MRO_QM_OFFICER,
        ROLES.STORE_INSPECTOR,
        ROLES.STORE_EXECUTIVE,
        ROLES.SAFETY_COFS,
        ROLES.SAFETY_MANAGER,
        ROLES.SAFETY_OFFICER,
        ROLES.MIS,
        ROLES.SPECTATOR
      ],
      requireHrms: true
    },
    {
      text: 'Staff training',
      icon: <WorkspacePremium />,
      path: '/staff-training',
      roles: [
        ROLES.SUPER_ADMIN,
        ROLES.ACCOUNTABLE_MANAGER,
        ROLES.HR_MANAGER,
        ROLES.OPERATIONS_MANAGER,
        ROLES.OPERATIONS_EXECUTIVE,
        ROLES.SALES,
        ROLES.PLANNING_MANAGER,
        ROLES.PLANNING_OFFICER,
        ROLES.CA_MANAGER,
        ROLES.CA_TECHNICAL_OFFICER,
        ROLES.CAMO_QM_MANAGER,
        ROLES.CAMO_QM_OFFICER,
        ROLES.MRO_MANAGER,
        ROLES.MRO_ENGINEER,
        ROLES.MRO_QM_MANAGER,
        ROLES.MRO_QM_OFFICER,
        ROLES.STORE_INSPECTOR,
        ROLES.STORE_EXECUTIVE,
        ROLES.SAFETY_COFS,
        ROLES.SAFETY_MANAGER,
        ROLES.SAFETY_OFFICER,
        ROLES.MIS,
        ROLES.SPECTATOR
      ],
      /** Shown only when the full Staff details suite is off */
      requireHrmsOff: true
    },
    { text: 'Flight Entry', icon: <FlightTakeoff />, path: '/sales', roles: [ROLES.SALES, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
    { text: 'Operations Board', icon: <Assignment />, path: '/ops', roles: [ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
    {
      text: 'CAMO',
      icon: <FlightTakeoff />,
      children: [
        { text: 'CAMO Dashboard', icon: <Assessment />, path: '/camo-dashboard', roles: [ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER] },
        { text: 'Aircraft Life-cycle Management', icon: <PrecisionManufacturing />, path: '/aircraft-lifecycle', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE, ROLES.PLANNING_MANAGER, ROLES.PLANNING_OFFICER] },
        { text: 'Master Data Management', icon: <AdminPanelSettings />, path: '/master-data', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.MRO_MANAGER, ROLES.STORE_INSPECTOR] },
        { text: 'Maintenance & Inspections', icon: <Build />, path: '/maintenance', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.STORE_INSPECTOR, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER] },
        { text: 'AMP Schedules', icon: <Build />, path: '/amp-schedules', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.STORE_INSPECTOR, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER] },
        { text: 'AD/SB Service Bulletin', icon: <Build />, path: '/adsb-service-bulletin', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER] },
        { text: 'Technical Log Book', icon: <Book />, path: '/technical-log-book', roles: [ROLES.CA_TECHNICAL_OFFICER, ROLES.CA_MANAGER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE, ROLES.SUPER_ADMIN, ROLES.CREW, ROLES.LOAN_PILOT, ROLES.EXTERNAL_PILOT, ROLES.PLANNING_MANAGER, ROLES.PLANNING_OFFICER, ROLES.MRO_ENGINEER, ROLES.MRO_MANAGER, ROLES.ACCOUNTABLE_MANAGER] },
        { text: 'Technical Log Book Format', icon: <AdminPanelSettings />, path: '/admin/technical-log-book-format', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER] },
      ]
    },
    { text: 'Planning', icon: <Assignment />, path: '/planning', roles: [ROLES.PLANNING_MANAGER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER] },
    {
      text: 'PROCUREMENT AND STORE',
      icon: <ShoppingCart />,
      children: [
        { text: 'Store Activities', icon: <Inventory2 />, path: '/store', roles: [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.MRO_MANAGER, ROLES.PLANNING_MANAGER, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
        { text: 'Store Inventory', icon: <Inventory2 />, path: '/store/inventory', roles: [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.MRO_MANAGER, ROLES.PLANNING_MANAGER, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
        { text: 'Store Returns', icon: <LocalShipping />, path: '/store/returns', roles: [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.MRO_MANAGER, ROLES.PLANNING_MANAGER, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
        { text: 'Receiving and Inspection', icon: <Inbox />, path: '/receiving-and-inspection', roles: [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.CA_MANAGER, ROLES.MRO_MANAGER, ROLES.CAMO_QM_MANAGER, ROLES.PLANNING_MANAGER, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
        { text: 'Issuance', icon: <LocalShipping />, path: '/issuance', roles: [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.MRO_MANAGER, ROLES.PLANNING_MANAGER, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
        { text: 'Procurement', icon: <ReceiptLong />, path: '/procurement', roles: [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.MRO_MANAGER, ROLES.PLANNING_MANAGER, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] },
        { text: 'Loan', icon: <SwapHoriz />, path: '/loan', roles: [ROLES.STORE_EXECUTIVE, ROLES.STORE_INSPECTOR, ROLES.MRO_MANAGER, ROLES.PLANNING_MANAGER, ROLES.ACCOUNTABLE_MANAGER, ROLES.SUPER_ADMIN] }
      ]
    },
    {
      text: 'ADMIN',
      icon: <AdminPanelSettings />,
      children: [
        { text: 'User', icon: <People />, path: '/users', roles: [ROLES.SUPER_ADMIN, ROLES.OPERATIONS_MANAGER] },
        { text: hrmsEnabled ? 'People' : 'User Onboarding', icon: <AssignmentInd />, path: '/users/onboard', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.HR_MANAGER] },
        { text: 'Client Settings', icon: <Settings />, path: '/client-settings', roles: [ROLES.SUPER_ADMIN] },
        { text: 'Audit Logs', icon: <History />, path: '/audit', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.OPERATIONS_MANAGER, ROLES.OPERATIONS_EXECUTIVE] },
        { text: 'MIS Dashboard', icon: <Assessment />, path: '/mis-dashboard', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.MIS] },
        { text: 'Roles Glossary', icon: <Security />, path: '/roles-glossary', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER] },
      ]
    },
    { text: 'Crew Inbox', icon: <Person />, path: '/crew', roles: [ROLES.CREW, ROLES.LOAN_PILOT, ROLES.EXTERNAL_PILOT] },
    { text: 'Dashboard', icon: <Dashboard />, path: '/client-portal', roles: [ROLES.CLIENT] },
    { text: 'Operator Portal', icon: <Engineering />, path: '/operator-portal', roles: [ROLES.EXTERNAL_OPERATOR] },
    { text: 'Fleet Portal', icon: <Flight />, path: '/client-portal', roles: [ROLES.SPECTATOR] },
    { text: 'Aircraft', icon: <AirplanemodeActive />, path: '/aircraft', excludeRoles: [ROLES.CREW, ROLES.LOAN_PILOT, ROLES.EXTERNAL_PILOT, ROLES.SALES, ROLES.CLIENT, ROLES.EMPLOYEE, ROLES.EXTERNAL_OPERATOR] },
    { text: 'Serialized Item Lifecycle', icon: <History />, path: '/serialized-items', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE] },
    { text: 'Parts Catalog', icon: <Category />, path: '/parts-catalog', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE] },
    { text: 'MRO Workbench', icon: <Engineering />, path: '/mro', roles: [ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER] },
    {
      text: 'Authorization',
      icon: <WorkspacePremium />,
      roles: AUTHORIZATION_MENU_ROLES,
      children: [
        {
          text: 'Certifying Staff',
          icon: <WorkspacePremium />,
          path: '/authorization',
          roles: AUTHORIZATION_MENU_ROLES
        },
        {
          text: 'Non-Certifying Staff',
          icon: <WorkspacePremium />,
          path: '/authorization/non-certifying',
          roles: AUTHORIZATION_MENU_ROLES
        },
        {
          text: 'Instructor',
          icon: <WorkspacePremium />,
          path: '/authorization/instructor',
          roles: AUTHORIZATION_MENU_ROLES
        },
        {
          text: 'One-Off',
          icon: <WorkspacePremium />,
          path: '/authorization/one-off',
          roles: AUTHORIZATION_MENU_ROLES
        }
      ]
    },
    {
      text: 'MRO Workbench Lab',
      icon: <Engineering />,
      children: [
        { text: 'Lab Overview', icon: <Dashboard />, path: '/mro/lab', roles: [ROLES.SPECTATOR] },
        { text: 'Lab Planning', icon: <Assignment />, path: '/mro/lab/planning', roles: [ROLES.SPECTATOR] },
        { text: 'Lab Manager', icon: <Engineering />, path: '/mro/lab/manager', roles: [ROLES.SPECTATOR] },
        { text: 'Lab Engineer', icon: <Build />, path: '/mro/lab/engineer', roles: [ROLES.SPECTATOR] },
        { text: 'Lab Quality', icon: <Security />, path: '/mro/lab/qm', roles: [ROLES.SPECTATOR] }
      ]
    },
    { text: 'My Profile', icon: <Person />, path: '/profile', excludeRoles: [ROLES.SUPER_ADMIN, ROLES.SPECTATOR] },
    { text: 'DGCA Baseline', icon: <Settings />, path: '/dgca-baseline', roles: [ROLES.SUPER_ADMIN] },
    { text: 'Operational Queries', icon: <Feedback />, path: '/feedback', roles: [] }, // Empty array = all roles
    { text: 'PDF Field Mappings', icon: <Description />, path: '/pdf/field-mappings', roles: [ROLES.SPECTATOR] },
    { text: 'PDF Template Testing', icon: <BugReport />, path: '/pdf/template-testing', roles: [ROLES.SPECTATOR] },
    { text: 'Aviation Glossary', icon: <MenuBook />, path: '/glossary', roles: [] }, // Empty array = all roles
    { text: 'Fleetwright Tutorials', icon: <School />, path: '/fleetwright-tutorials', roles: [] }, // Empty array = all roles
    { text: 'Report Center', icon: <Assessment />, path: '/report-center', roles: [ROLES.SPECTATOR, ROLES.SUPER_ADMIN, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.PLANNING_MANAGER, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.MRO_QM_MANAGER, ROLES.MRO_QM_OFFICER, ROLES.STORE_INSPECTOR, ROLES.SAFETY_COFS, ROLES.SAFETY_MANAGER, ROLES.SAFETY_OFFICER] },
    { text: 'Spect Control', icon: <Security />, path: '/spect-control', roles: [ROLES.SPECTATOR] },
    { text: 'Technical Support', icon: <SupportAgent />, path: '/technical-support', roles: [] }, // Empty array = all roles
    { text: 'Quick Add', icon: <Add />, path: '/quick-add', roles: [ROLES.SUPER_ADMIN, ROLES.ACCOUNTABLE_MANAGER, ROLES.CA_MANAGER, ROLES.CA_TECHNICAL_OFFICER, ROLES.PLANNING_MANAGER, ROLES.CAMO_QM_MANAGER, ROLES.CAMO_QM_OFFICER, ROLES.MRO_MANAGER, ROLES.MRO_ENGINEER, ROLES.STORE_INSPECTOR, ROLES.STORE_EXECUTIVE] }
  ];

  const isVisible = (item) => {
    if (item.requireHrms && !hrmsEnabled) {
      return false;
    }
    if (item.requireHrmsOff && hrmsEnabled) {
      return false;
    }
    if (user?.role === ROLES.CLIENT) {
      return item.roles?.includes(ROLES.CLIENT) === true || isAuthorizationNavItem(item);
    }
    if (user?.role === ROLES.EXTERNAL_OPERATOR) {
      return item.roles?.includes(ROLES.EXTERNAL_OPERATOR) === true
        || item.path === '/profile'
        || item.path === '/glossary'
        || item.path === '/fleetwright-tutorials'
        || item.path === '/technical-support'
        || isAuthorizationNavItem(item);
    }
    if (user?.role === ROLES.EMPLOYEE) {
      return item.path === '/profile' || item.path === '/notice-board' || isAuthorizationNavItem(item);
    }
    if (item.excludeRoles && item.excludeRoles.includes(user?.role)) {
      return false;
    }
    if (item.text === 'Quick Add' && isReadOnly) {
      return false;
    }
    const moduleKey = item.path ? getModuleKeyFromPath(item.path) : null;
    const roleAllowed = !item.roles || item.roles.length === 0 || roleMatches(user?.role, item.roles);
    if (moduleKey && canViewModuleByGrant(user?.role, viewGrants, moduleKey, roleAllowed)) {
      return true;
    }
    if (!item.roles || item.roles.length === 0) {
      return true;
    }
    return roleAllowed;
  };

  const filteredMenuItems = menuItems
    .map((item) => {
      let next = item;
      if (trimHangarNav) {
        if (item.children && item.text === 'CAMO') {
          const children = item.children.filter((c) => hangarAllowedCamoChildren.has(c.path));
          if (!children.length) return null;
          next = { ...item, text: 'Fleet register', children };
        } else if (item.path && hangarHiddenPaths.has(item.path)) {
          const keepForOps = hangarOpsKeptRoles.has(user?.role) && hangarOpsKeptPaths.has(item.path);
          const keepForCrew = [ROLES.CREW, ROLES.LOAN_PILOT].includes(user?.role) && hangarCrewKeptPaths.has(item.path);
          const keepForEdr =
            hangarMaintenanceKeptRoles.has(user?.role) && hangarMaintenanceKeptPaths.has(item.path);
          if (!keepForOps && !keepForCrew && !keepForEdr) {
            return null;
          }
        }
      }
      if (next.children) {
        const visibleChildren = next.children.filter(isVisible);
        if (visibleChildren.length === 0) return null;
        return { ...next, children: visibleChildren };
      }
      return isVisible(next) ? next : null;
    })
    .filter(Boolean);

  const [openGroups, setOpenGroups] = useState({});

  const handleGroupToggle = (text) => {
    setOpenGroups(prev => ({ ...prev, [text]: !prev[text] }));
  };

  const isChildPathActive = (childPath, pathname) => {
    if (pathname === childPath) return true;
    if (childPath === '/store' && pathname.startsWith('/store/')) return true;
    if (childPath === '/users/onboard' && pathname.startsWith('/users/onboard')) return true;
    // Overview hub must not stay selected on /mro/lab/planning etc.
    if (childPath === '/mro/lab') return pathname === '/mro/lab';
    return false;
  };

  useEffect(() => {
    menuItems.forEach(item => {
      if (item.children?.some(c => isChildPathActive(c.path, location.pathname))) {
        setOpenGroups(prev => (prev[item.text] ? prev : { ...prev, [item.text]: true }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarMinimizeToggle = () => {
    setIsSidebarMinimized((prev) => !prev);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
  };

  const drawer = (
    <Box
      sx={{
        bgcolor: drawerBackground,
        minHeight: '100%'
      }}
    >
      <Toolbar sx={{ bgcolor: 'transparent' }}>
        {!isSidebarMinimized && (
          <Box
            component="img"
            src={fleetwrightLogo}
            alt="Fleet Wright"
            sx={{
              width: '100%',
              maxWidth: 190,
              height: 'auto'
            }}
          />
        )}
      </Toolbar>
      <List>
        {filteredMenuItems.map((item) => {
          if (item.children) {
            const open = !!openGroups[item.text];
            const hasActiveChild = item.children.some(c => isChildPathActive(c.path, location.pathname));
            return (
              <React.Fragment key={item.text}>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleGroupToggle(item.text)}
                    selected={isSidebarMinimized && hasActiveChild}
                    sx={{
                      minHeight: 48,
                      justifyContent: isSidebarMinimized ? 'center' : 'initial',
                      px: isSidebarMinimized ? 2 : 2.5
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isSidebarMinimized ? 0 : 2,
                        justifyContent: 'center',
                        ...menuIconSx(item.text),
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {!isSidebarMinimized && (
                      <>
                        <ListItemText
                          primary={item.text}
                          primaryTypographyProps={{ fontWeight: 'bold' }}
                        />
                        {open ? <ExpandLess /> : <ExpandMore />}
                      </>
                    )}
                  </ListItemButton>
                </ListItem>
                <Collapse in={open && !isSidebarMinimized} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map(child => (
                      <ListItemButton
                        key={child.text}
                        selected={isChildPathActive(child.path, location.pathname)}
                        onClick={() => navigate(child.path)}
                        sx={{
                          pl: 4,
                          minHeight: 44
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: 2,
                            justifyContent: 'center',
                            ...menuIconSx(child.text),
                          }}
                        >
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText primary={child.text} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </React.Fragment>
            );
          }

          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(`${item.path}/`))}
                onClick={() => navigate(item.path)}
                sx={{
                  minHeight: 48,
                  justifyContent: isSidebarMinimized ? 'center' : 'initial',
                  px: isSidebarMinimized ? 2 : 2.5
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: isSidebarMinimized ? 0 : 2,
                    justifyContent: 'center',
                    ...menuIconSx(item.text),
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {!isSidebarMinimized && <ListItemText primary={item.text} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={4}
        color="primary"
        sx={{
          width: { sm: `calc(100% - ${isSidebarMinimized ? miniDrawerWidth : drawerWidth}px)` },
          ml: { sm: `${isSidebarMinimized ? miniDrawerWidth : drawerWidth}px` }
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label={isSidebarMinimized ? 'expand sidebar' : 'minimize sidebar'}
            onClick={handleSidebarMinimizeToggle}
            sx={{ mr: 1, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {isSidebarMinimized ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
          Fleetwright India Pvt Ltd
          </Typography>
          <NotificationsBell />
          <Button
            color="inherit"
            onClick={handleMenuClick}
            startIcon={
              <Avatar
                sx={{
                  width: 32,
                  height: 32
                }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            }
          >
            {user?.firstName} {user?.lastName}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <ExitToApp fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: isSidebarMinimized ? miniDrawerWidth : drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: drawerBackground,
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isSidebarMinimized ? miniDrawerWidth : drawerWidth,
              overflowX: 'hidden',
              bgcolor: drawerBackground,
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${isSidebarMinimized ? miniDrawerWidth : drawerWidth}px)` }
        }}
      >
        <Toolbar />
        {isReadOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You are signed in as a Spectator with read-only access. Create, edit, and delete actions are disabled.
          </Alert>
        )}
        {children}
        {user?.role !== ROLES.EMPLOYEE && user?.role !== ROLES.EXTERNAL_OPERATOR && <FloatingFeedbackButton />}
      </Box>
    </Box>
  );
};

export default Layout;